import { Container, ContainerSize, Heading, Text } from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const ContainerPage = () => {
    return (
        <>
            <Container size={ContainerSize.Fluid}>
                <Heading el="h1" className="fw-semibold mb-3 ls-1">
                    Container
                </Heading>
                <Text className="mb-4">
                    <Text el="strong">Container</Text> is a component that provides a responsive fixed width container
                    for your content. It can be used to center your content and provide consistent spacing.
                </Text>

                <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                    Default Container
                </Heading>
                <Text className="mb-4">
                    The default <Text el="strong">Container</Text> has a responsive max-width that changes at each breakpoint.
                    It provides consistent horizontal padding and centers your content.
                </Text>

                <CodeSnippet>
                    <CodePreview>
                        <Container>
                            <div className="p-3 border border-primary bg-primary bg-opacity-10">
                                Default Container - Responsive fixed width
                            </div>
                        </Container>
                    </CodePreview>
                    <CodeHighlighter language="tsx">
                        {DefaultContainerCode}
                    </CodeHighlighter>
                </CodeSnippet>

                <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                    Fluid Container
                </Heading>

                <Text className="mb-4">
                    A fluid container <Text el="strong">(ContainerSize.Fluid)</Text> always spans the
                    entire width of the viewport, regardless of the device size or breakpoint. Unlike responsive containers, it never
                    becomes fixed — it's always 100% wide.
                </Text>

                <CodeSnippet>
                    <CodePreview>
                        <Container size={ContainerSize.Fluid}>
                            <div className="p-3 border border-success bg-success bg-opacity-10">
                                Fluid Container - Full width (100%)
                            </div>
                        </Container>
                    </CodePreview>
                    <CodeHighlighter language="tsx">
                        {FluidContainerCode}
                    </CodeHighlighter>
                </CodeSnippet>

                <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                    Responsive Containers
                </Heading>

                <Text className="mb-4">
                    Responsive <Text el="strong">Containers</Text> adapt their width based on the selected
                    breakpoint. Each size option makes the container stretch to 100% width until that breakpoint is
                    reached, then becomes fixed and centered.
                </Text>

                <CodeSnippet>
                    <CodePreview>
                        <div className="mb-3">
                            <Text className="small text-muted mb-1">Small (sm) - 100% until ≥576px</Text>
                            <Container size={ContainerSize.Sm}>
                                <div className="p-3 border border-info bg-info bg-opacity-10">
                                    Container Sm
                                </div>
                            </Container>
                        </div>

                        <div className="mb-3">
                            <Text className="small text-muted mb-1">Medium (md) - 100% until ≥768px</Text>
                            <Container size={ContainerSize.Md}>
                                <div className="p-3 border border-info bg-info bg-opacity-10">
                                    Container Md
                                </div>
                            </Container>
                        </div>

                        <div className="mb-3">
                            <Text className="small text-muted mb-1">Large (lg) - 100% until ≥992px</Text>
                            <Container size={ContainerSize.Lg}>
                                <div className="p-3 border border-info bg-info bg-opacity-10">
                                    Container Lg
                                </div>
                            </Container>
                        </div>

                        <div className="mb-3">
                            <Text className="small text-muted mb-1">Extra Large (xl) - 100% until ≥1200px</Text>
                            <Container size={ContainerSize.Xl}>
                                <div className="p-3 border border-info bg-info bg-opacity-10">
                                    Container Xl
                                </div>
                            </Container>
                        </div>

                        <div>
                            <Text className="small text-muted mb-1">Extra Extra Large (xxl) - 100% until ≥1400px</Text>
                            <Container size={ContainerSize.Xxl}>
                                <div className="p-3 border border-info bg-info bg-opacity-10">
                                    Container Xxl
                                </div>
                            </Container>
                        </div>
                    </CodePreview>
                    <CodeHighlighter language="tsx">
                        {ResponsiveContainerCode}
                    </CodeHighlighter>
                </CodeSnippet>

                <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                    Comparison
                </Heading>
                <Text className="mb-4">
                    See how different container sizes compare side by side:
                </Text>
                <CodeSnippet>
                    <CodePreview>
                        <div className="mb-2">
                            <Container>
                                <div className="p-2 border border-primary bg-primary bg-opacity-10 text-center">
                                    Default Container
                                </div>
                            </Container>
                        </div>
                        <div className="mb-2">
                            <Container size={ContainerSize.Fluid}>
                                <div className="p-2 border border-success bg-success bg-opacity-10 text-center">
                                    Fluid Container
                                </div>
                            </Container>
                        </div>
                        <div className="mb-2">
                            <Container size={ContainerSize.Lg}>
                                <div className="p-2 border border-info bg-info bg-opacity-10 text-center">
                                    Container Lg
                                </div>
                            </Container>
                        </div>
                    </CodePreview>
                    <CodeHighlighter language="tsx">
                        {ComparisonCode}
                    </CodeHighlighter>
                </CodeSnippet>
            </Container>
        </>
    );
};

export default ContainerPage;

const DefaultContainerCode = `<Container>
    <div>Default Container - Responsive fixed width</div>
</Container>`;

const FluidContainerCode = `<Container size={ContainerSize.Fluid}>
    <div>Fluid Container - Full width (100%)</div>
</Container>`;

const ResponsiveContainerCode = `<Container size={ContainerSize.Sm}>
    100% wide until small breakpoint (≥576px)
</Container>

<Container size={ContainerSize.Md}>
    100% wide until medium breakpoint (≥768px)
</Container>

<Container size={ContainerSize.Lg}>
    100% wide until large breakpoint (≥992px)
</Container>

<Container size={ContainerSize.Xl}>
    100% wide until extra large breakpoint (≥1200px)
</Container>

<Container size={ContainerSize.Xxl}>
    100% wide until extra extra large breakpoint (≥1400px)
</Container>`;

const ComparisonCode = `<Container>
    <div>Default Container</div>
</Container>

<Container size={ContainerSize.Fluid}>
    <div>Fluid Container</div>
</Container>

<Container size={ContainerSize.Lg}>
    <div>Container Lg</div>
</Container>`;
